import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileUp, Pencil, Printer, Trash2, Undo2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { base44 } from "@/api/base44Client";
import { escapePrintHtml } from "@/lib/clerkStatements";
import { isPdfFile, statementFileUrl } from "@/lib/feeSlipStatements";
import {
  addRecoupInvoice,
  buildRecoupInvoice,
  buildRecoupRows,
  editRecoupInvoice,
  parseInvoiceText,
  recoupStageLabel,
  recoupStages,
  removeRecoupInvoice,
  setEntryNote,
  setEntryStage,
  summariseRecoup,
  updateRecoupInvoice,
} from "@/lib/budgetRecoup";

const money = (value) => (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const day = (value) => {
  const iso = String(value || "").slice(0, 10);
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};
const todayIso = () => new Date().toISOString().slice(0, 10);

const SOURCE_LABELS = {
  upload: "Uploaded",
  clerk_statement: "Clerk statement",
  fee_slip: "Fee slip",
  email: "From email",
  invoice: "Counsel invoice",
};

const STAGE_STYLE = {
  paid_from_budget: "border-rose-200 bg-rose-50 text-rose-800",
  retro_aid_required: "border-amber-200 bg-amber-50 text-amber-800",
  aid_applied: "border-sky-200 bg-sky-50 text-sky-800",
  recovered: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

const BLANK_FORM = {
  clientName: "", payee: "", invoiceRef: "", invoiceDate: "", paidOn: "", amount: "",
  stage: "paid_from_budget", note: "",
};

async function readPdfText(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("The file could not be read"));
    reader.readAsDataURL(file);
  });
  // Loaded only when someone uploads, so the PDF reader does not weigh down the screen.
  const { extractPdfTextWithPdfJs } = await import("@/lib/standaloneServices");
  return extractPdfTextWithPdfJs(dataUrl);
}

export default function BudgetRecoupPanel({ brandPrefix, recoup, screenRows, userName }) {
  const { data, loaded, loadError, saving, change } = recoup;
  const stages = useMemo(() => recoupStages(brandPrefix), [brandPrefix]);
  const rows = useMemo(() => buildRecoupRows(data, screenRows), [data, screenRows]);
  const summary = useMemo(() => summariseRecoup(rows, brandPrefix), [rows, brandPrefix]);

  const [stageFilter, setStageFilter] = useState("outstanding");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState("");
  const [form, setForm] = useState(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  // The uploaded invoice being corrected: { id, form, file } - file is a replacement PDF.
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const visibleRows = rows.filter((row) => {
    if (stageFilter === "all") return true;
    if (stageFilter === "outstanding") return row.stage !== "recovered";
    return row.stage === stageFilter;
  });

  const save = async (apply, success) => {
    try {
      await change(apply);
      if (success) toast.success(success);
      return true;
    } catch (err) {
      toast.error(err.message || "Recover from aid could not be saved.");
      return false;
    }
  };

  const chooseFile = async (chosen) => {
    if (!chosen) return;
    if (!isPdfFile(chosen)) {
      toast.error("Choose a PDF invoice.");
      return;
    }
    setFile(chosen);
    setForm({ ...BLANK_FORM, paidOn: todayIso() });
    setReadNote("");
    setReading(true);
    try {
      const found = parseInvoiceText(await readPdfText(chosen));
      const filled = Object.entries(found).filter(([, value]) => value).map(([key]) => key);
      setForm((current) => ({
        ...current,
        ...Object.fromEntries(Object.entries(found).filter(([, value]) => value)),
      }));
      setReadNote(filled.length
        ? "Details were read from the PDF. Check each one before saving."
        : "No details could be read from this PDF (it may be a scan). Type them in below.");
    } catch (err) {
      console.warn("Invoice text could not be read:", err);
      setReadNote("No details could be read from this PDF. Type them in below.");
    } finally {
      setReading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setForm(BLANK_FORM);
    setReadNote("");
    setUploadOpen(false);
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!file) return toast.error("Choose the invoice PDF first.");
    if (!form.clientName.trim()) return toast.error("Enter the client's name.");
    if (!(Number.parseFloat(String(form.amount).replace(/[$,\s]/g, "")) > 0)) {
      return toast.error("Enter the invoice amount.");
    }
    setSubmitting(true);
    try {
      const upload = await base44.integrations.Core.UploadFile({ file });
      const invoice = buildRecoupInvoice(
        { ...form, fileName: file.name, fileUrl: upload?.file_url, filePath: upload?.storage_path },
        { by: userName },
      );
      if (await save((current) => addRecoupInvoice(current, invoice), "Invoice added to Recover from aid.")) {
        resetUpload();
      }
    } catch (err) {
      toast.error(err.message || "The invoice could not be uploaded.");
    } finally {
      setSubmitting(false);
    }
  };

  const changeStage = (row, stage) => {
    if (row.kind === "uploaded") {
      // When and by whom, so the weekly summary can say which week it moved in.
      return save((current) => updateRecoupInvoice(current, row.id, { stage, stageAt: new Date().toISOString(), stageBy: userName }));
    }
    const live = screenRows.find((item) => item.id === row.id);
    return save((current) => setEntryStage(current, live || {
      id: row.id,
      clientName: row.clientName,
      counselName: row.payee,
      invoiceRef: row.invoiceRef,
      invoiceAmount: row.amount,
      source: row.source,
    }, stage, { by: userName }));
  };

  const saveNote = (row) => {
    const note = noteDrafts[row.id];
    if (note === undefined || note === (row.note || "")) return;
    const apply = row.kind === "uploaded"
      ? (current) => updateRecoupInvoice(current, row.id, { note: String(note).slice(0, 500) })
      : (current) => setEntryNote(current, row.id, note);
    save(apply).then((ok) => {
      if (ok) setNoteDrafts(({ [row.id]: _saved, ...rest }) => rest);
    });
  };

  const startEdit = (row) => setEditing({
    id: row.id,
    file: null,
    form: {
      clientName: row.clientName === "Unnamed client" ? "" : row.clientName || "",
      payee: row.payee || "",
      invoiceRef: row.invoiceRef || "",
      invoiceDate: row.invoiceDate || "",
      paidOn: row.paidOn || "",
      amount: row.amount ? String(row.amount) : "",
      note: noteDrafts[row.id] ?? row.note ?? "",
    },
  });

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editing) return;
    const { id, form: fields, file: replacement } = editing;
    if (!fields.clientName.trim()) return toast.error("Enter the client's name.");
    if (!(Number.parseFloat(String(fields.amount).replace(/[$,\s]/g, "")) > 0)) {
      return toast.error("Enter the invoice amount.");
    }
    if (replacement && !isPdfFile(replacement)) return toast.error("Choose a PDF invoice.");
    setSavingEdit(true);
    try {
      let fileFields = {};
      if (replacement) {
        const upload = await base44.integrations.Core.UploadFile({ file: replacement });
        fileFields = { fileName: replacement.name, fileUrl: upload?.file_url, filePath: upload?.storage_path };
      }
      const ok = await save(
        (current) => editRecoupInvoice(current, id, { ...fields, ...fileFields }, { by: userName }),
        "Invoice updated.",
      );
      if (ok) {
        setEditing(null);
        setNoteDrafts(({ [id]: _edited, ...rest }) => rest);
      }
    } catch (err) {
      toast.error(err.message || "The new PDF could not be uploaded.");
    } finally {
      setSavingEdit(false);
    }
  };

  const editField = (key, label, props = {}) => (
    <div>
      <Label htmlFor={`recoup-edit-${key}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</Label>
      <Input
        id={`recoup-edit-${key}`}
        value={editing?.form[key] ?? ""}
        onChange={(event) => setEditing((current) => ({ ...current, form: { ...current.form, [key]: event.target.value } }))}
        className="mt-1 h-8 text-xs bg-white"
        {...props}
      />
    </div>
  );

  const removeRow = (row) => {
    if (row.kind === "uploaded") {
      if (!window.confirm(`Remove the invoice for ${row.clientName} (${money(row.amount)}) from Recover from aid?`)) return;
      save((current) => removeRecoupInvoice(current, row.id), "Invoice removed.");
    } else {
      save((current) => setEntryStage(current, { id: row.id }, ""), "No longer tracked for recovery.");
    }
  };

  const printTally = () => {
    const win = window.open("", "_blank");
    if (!win) return toast.error("Allow pop-ups to print.");
    const groups = summary.stages
      .filter((stage) => stage.items.length)
      .map((stage) => `
        <h2>${escapePrintHtml(stage.label)} <span>${stage.count} &middot; ${escapePrintHtml(money(stage.total))}</span></h2>
        <table><thead><tr><th>Client</th><th>Paid to</th><th>Invoice</th><th>Date</th><th>Note</th><th class="r">Amount</th></tr></thead>
        <tbody>${stage.items.map((row) => `<tr>
          <td>${escapePrintHtml(row.clientName)}</td><td>${escapePrintHtml(row.payee)}</td>
          <td>${escapePrintHtml(row.invoiceRef)}</td><td>${escapePrintHtml(day(row.date))}</td>
          <td>${escapePrintHtml(row.note)}</td><td class="r">${escapePrintHtml(money(row.amount))}</td></tr>`).join("")}
        </tbody></table>`).join("");
    win.document.write(`<!doctype html><html><head><title>${escapePrintHtml(brandPrefix)} - Recover from aid</title><style>
      body{font-family:Arial,sans-serif;font-size:11px;color:#0f172a;margin:16mm}
      h1{font-size:16px;margin:0 0 4px}h2{font-size:12px;margin:16px 0 4px}h2 span{font-weight:normal;color:#475569}
      table{width:100%;border-collapse:collapse}th,td{border:1px solid #cbd5e1;padding:4px 6px;text-align:left;vertical-align:top}
      th{background:#f1f5f9}.r{text-align:right}.total{margin-top:14px;font-weight:bold;font-size:12px}
    </style></head><body>
      <h1>${escapePrintHtml(brandPrefix)} &mdash; invoices paid from budget, to recover from aid</h1>
      <div>Printed ${escapePrintHtml(new Date().toLocaleString("en-AU"))}</div>
      ${groups || "<p>Nothing is being tracked.</p>"}
      <div class="total">Still to recover: ${escapePrintHtml(money(summary.outstandingTotal))} (${summary.outstandingCount} invoice${summary.outstandingCount === 1 ? "" : "s"})</div>
      <div class="total">Recovered: ${escapePrintHtml(money(summary.recoveredTotal))}</div>
      <script>window.addEventListener("load",()=>window.print());<\/script>
    </body></html>`);
    win.document.close();
  };

  const field = (key, label, props = {}) => (
    <div>
      <Label htmlFor={`recoup-${key}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</Label>
      <Input
        id={`recoup-${key}`}
        value={form[key]}
        onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
        className="mt-1 h-8 text-xs"
        {...props}
      />
    </div>
  );

  return (
    <Card className="shadow-sm border-slate-200" id="recover-from-aid">
      <CardHeader className="py-4 border-b border-slate-100 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="text-sm font-bold text-slate-800">Recover from aid</CardTitle>
          <CardDescription className="text-xs text-slate-500">
            Outside invoices {brandPrefix} has paid from its own budget, tracked until retrospective aid pays them back.
            Upload an invoice here, or open any invoice below and choose a stage under "Recover from aid".
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={printTally} className="h-8 gap-1 px-3 text-[11px]">
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
          <Button
            type="button"
            onClick={() => setUploadOpen((open) => !open)}
            disabled={!loaded || loadError}
            className="h-8 gap-1 px-3 text-[11px] bg-slate-800 text-white hover:bg-slate-900"
          >
            <FileUp className="h-3.5 w-3.5" /> Upload an invoice
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {loadError && (
          <p className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            Recover from aid could not be loaded. Nothing can be changed until it loads; reload the page to try again.
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          <button
            type="button"
            onClick={() => setStageFilter("outstanding")}
            aria-pressed={stageFilter === "outstanding"}
            className={`rounded-lg border p-3 text-left ${stageFilter === "outstanding" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 bg-white"}`}
          >
            <span className="block text-[10px] font-bold uppercase tracking-wide opacity-80">Still to recover</span>
            <span className="block text-base font-bold">{money(summary.outstandingTotal)}</span>
            <span className="block text-[10px] opacity-80">{summary.outstandingCount} invoice{summary.outstandingCount === 1 ? "" : "s"}</span>
          </button>
          {summary.stages.map((stage) => (
            <button
              key={stage.value}
              type="button"
              onClick={() => setStageFilter(stageFilter === stage.value ? "outstanding" : stage.value)}
              aria-pressed={stageFilter === stage.value}
              className={`rounded-lg border p-3 text-left ${STAGE_STYLE[stage.value]} ${stageFilter === stage.value ? "ring-2 ring-slate-700" : ""}`}
            >
              <span className="block text-[10px] font-bold uppercase tracking-wide">{stage.label}</span>
              <span className="block text-base font-bold">{money(stage.total)}</span>
              <span className="block text-[10px]">{stage.count} invoice{stage.count === 1 ? "" : "s"}</span>
            </button>
          ))}
        </div>

        {uploadOpen && (
          <form onSubmit={submitUpload} className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
            <div>
              <Label htmlFor="recoup-file" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Invoice PDF</Label>
              <Input
                id="recoup-file"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(event) => chooseFile(event.target.files?.[0])}
                className="mt-1 h-9 text-xs bg-white"
              />
              {reading && <p className="mt-1 text-[11px] text-slate-500">Reading the invoice…</p>}
              {readNote && !reading && <p className="mt-1 text-[11px] text-amber-700">{readNote}</p>}
            </div>
            {file && !reading && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {field("clientName", "Client")}
                  {field("payee", "Paid to (counsel, clerk or supplier)")}
                  {field("invoiceRef", "Invoice number")}
                  {field("amount", "Amount (incl. GST)", { inputMode: "decimal" })}
                  {field("invoiceDate", "Invoice date", { type: "date" })}
                  {field("paidOn", `Date paid from ${brandPrefix} budget`, { type: "date" })}
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Stage</Label>
                    <Select value={form.stage} onValueChange={(stage) => setForm((current) => ({ ...current, stage }))}>
                      <SelectTrigger className="mt-1 h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stages.map((stage) => (
                          <SelectItem key={stage.value} value={stage.value} className="text-xs">{stage.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {field("note", "Note (e.g. aid number, date applied)")}
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting || saving} className="h-8 px-4 text-[11px] bg-slate-800 text-white hover:bg-slate-900">
                    {submitting ? "Saving…" : "Save invoice"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={resetUpload} className="h-8 px-3 text-[11px]">Cancel</Button>
                </div>
              </>
            )}
          </form>
        )}

        {!loaded ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : visibleRows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
            {rows.length === 0
              ? "Nothing is being tracked yet. Upload an invoice, or choose a stage on an invoice below."
              : "Nothing at this stage."}
          </p>
        ) : (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
            {visibleRows.map((row) => {
              const fileUrl = row.kind === "uploaded" ? statementFileUrl({ fileUrl: row.fileUrl }) : "";
              return (
                <div key={row.id}>
                <div className="grid gap-2 p-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] md:items-center">
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold text-slate-800">{row.clientName}</span>
                      <span className="text-xs font-semibold text-slate-700">{money(row.amount)}</span>
                      <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">
                        {SOURCE_LABELS[row.source] || "Invoice"}
                      </span>
                      {row.missing && (
                        <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800" title="This invoice is no longer in its source list; the figures shown are the ones saved when it was tracked.">
                          Not in current list
                        </span>
                      )}
                    </div>
                    <p className="truncate text-[11px] text-slate-500">
                      {[row.payee, row.invoiceRef && `Invoice ${row.invoiceRef}`, row.paidOn && `paid ${day(row.paidOn)}`]
                        .filter(Boolean).join(" · ")}
                    </p>
                    <Input
                      value={noteDrafts[row.id] ?? row.note ?? ""}
                      onChange={(event) => setNoteDrafts((current) => ({ ...current, [row.id]: event.target.value }))}
                      onBlur={() => saveNote(row)}
                      placeholder="Note (aid number, date applied…)"
                      aria-label={`Recovery note for ${row.clientName}`}
                      className="h-7 text-[11px]"
                    />
                  </div>
                  <Select value={row.stage} onValueChange={(stage) => changeStage(row, stage)} disabled={saving}>
                    <SelectTrigger className={`h-8 text-[11px] ${STAGE_STYLE[row.stage]}`} aria-label={`Recovery stage for ${row.clientName}`}>
                      <SelectValue>{recoupStageLabel(row.stage, brandPrefix)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.value} value={stage.value} className="text-xs">{stage.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    {fileUrl && (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 px-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                        title={row.fileName}
                      >
                        <Download className="h-3.5 w-3.5" /> PDF
                      </a>
                    )}
                    {row.kind === "uploaded" && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => (editing?.id === row.id ? setEditing(null) : startEdit(row))}
                        disabled={saving || savingEdit}
                        title="Edit this invoice's details"
                        aria-label={`Edit the invoice for ${row.clientName}`}
                        aria-expanded={editing?.id === row.id}
                        className="h-8 px-2 text-slate-500 hover:text-slate-800"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeRow(row)}
                      disabled={saving}
                      title={row.kind === "uploaded" ? "Remove this invoice" : "Stop tracking this invoice"}
                      className="h-8 px-2 text-slate-500 hover:text-rose-600"
                    >
                      {row.kind === "uploaded" ? <Trash2 className="h-3.5 w-3.5" /> : <Undo2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
                {editing?.id === row.id && (
                  <form onSubmit={saveEdit} className="space-y-3 border-t border-slate-100 bg-slate-50/70 p-3" aria-label={`Edit the invoice for ${row.clientName}`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {editField("clientName", "Client")}
                      {editField("payee", "Paid to (counsel, clerk or supplier)")}
                      {editField("invoiceRef", "Invoice number")}
                      {editField("amount", "Amount (incl. GST)", { inputMode: "decimal" })}
                      {editField("invoiceDate", "Invoice date", { type: "date" })}
                      {editField("paidOn", `Date paid from ${brandPrefix} budget`, { type: "date" })}
                      {editField("note", "Note (e.g. aid number, date applied)")}
                      <div>
                        <Label htmlFor="recoup-edit-file" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Replace the PDF (optional)
                        </Label>
                        <Input
                          id="recoup-edit-file"
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={(event) => {
                            const chosen = event.target.files?.[0] || null;
                            setEditing((current) => ({ ...current, file: chosen }));
                          }}
                          className="mt-1 h-9 text-xs bg-white"
                        />
                        {row.fileName && !editing.file && (
                          <p className="mt-1 truncate text-[10px] text-slate-500">Current: {row.fileName}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={savingEdit || saving} className="h-8 px-4 text-[11px] bg-slate-800 text-white hover:bg-slate-900">
                        {savingEdit ? "Saving…" : "Save changes"}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="h-8 px-3 text-[11px]">Cancel</Button>
                    </div>
                  </form>
                )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
